/**
 * @fileoverview `useShellTranslation()` — the shell chrome's translator.
 * @package @puffin-core/page-shell
 *
 * ## The bug this closes (AAS-I18N-002 Lane F-3)
 *
 * Shell primitives held their user-visible copy as PROP DEFAULTS — `label =
 * 'Show sidebar'`, `skipNavLabel = 'Skip to main content'`, module-level frozen
 * `DEFAULT_I18N` objects — documented as "i18n-ready: the caller overrides the
 * prop". A repo-wide sweep of every app's `src` tree found **no app passing any
 * of the shell-chrome props**. So all nine web apps rendered English chrome in a
 * fully translated page: AAS-I18N-001 §6.6 miss-class 1, shipped at scale and
 * invisible to a string-parity audit because the English lived in a default
 * parameter rather than in JSX.
 *
 * This is the same defect `PageShellLocaleContext.jsx` closed for `locale =
 * 'en-US'`, and it is closed the same way — by resolving at RENDER instead of
 * freezing at module load.
 *
 * ## Why importing the peerDep here is safe
 *
 * `lib/esm/index.js` used to say page-shell "deliberately does NOT ship a
 * `useShellTranslation()` hook" because react-i18next is a peerDep and requiring
 * it would break apps that had not wired i18n. Two things retired that argument:
 *
 * 1. `hooks/PageShellLocaleContext.jsx` already imports `useTranslation`, and a
 *    contract test asserts every app mounts it — so the dep is already hard in
 *    practice.
 * 2. Measured 2026-08-09: with **no `<I18nextProvider>` and no global instance**,
 *    react-i18next's `t` returns the supplied `defaultValue`, so an unwired consumer
 *    renders exactly the English it rendered before. (It also does not INTERPOLATE
 *    that default — see `fallbackT` below, which is why this hook returns a wrapper
 *    rather than react-i18next's `t` in that case.)
 *
 * ## Call contract — `defaultValue` is MANDATORY
 *
 * Always `t('appShell.skipNav', { defaultValue: 'Skip to main content' })` — a real
 * bundle key plus its English. Without a `defaultValue` an unwired tree renders the
 * raw dotted key. The `defaultValue` is a degrade path, **not** the source of truth:
 * `strings/en.js` is, and `tests/unit/strings/loadShellStrings.test.js` holds
 * en/es/en-XA at identical leaf paths. A key with no leaf would render its
 * `defaultValue` in every locale forever at a census of zero — AAS-I18N-001
 * miss-class 5 — so every key added here lands in all three bundles in the same edit.
 *
 * (The example above uses a REAL key on purpose: `shellKeyCoverage.test.js` scans
 * source TEXT, comments included, so a fictional key in a docblock fails it. That is
 * the scanner working, not a false positive.)
 *
 * ## Plurals — SUPPORTED since 2026-08-09 (Q-F3-P1)
 *
 * 🔴 **The previous revision of this docblock said "never add a `{{count}}` key
 * to this bundle". That is no longer true, and leaving it would have kept two
 * hand-rolled English plurals alive.** It was true only of the *old* parity
 * test, which compared raw leaf paths for strict equality across en / es /
 * en-XA — a rule Spanish cannot satisfy, because `Intl.PluralRules('es')`
 * needs `_many` where English has no such category. The bundle was never the
 * problem; the gate was. `loadShellStrings.test.js` now compares plural STEMS
 * and asserts each locale carries EXACTLY its own CLDR forms.
 *
 * A plural call site passes `count` plus one `defaultValue_<category>` per
 * category ENGLISH uses — `_one` and `_other`, never `_many`:
 *
 * ```js
 * t('controls.nResults', {
 *   count,
 *   defaultValue_one: '{{count}} result',
 *   defaultValue_other: '{{count}} results',
 * });
 * ```
 *
 * and the bundles carry `controls.nResults_one` / `_other` in `en.js` and
 * `en-XA.js`, plus `_many` in `es.js`.
 */

import { useTranslation } from 'react-i18next';

import { SHELL_NAMESPACE } from '../strings/namespace.js';

/**
 * The no-instance translator, imported rather than defined here.
 *
 * 🔴 Measured 2026-08-09, and it is the reason a wrapper exists at all:
 * react-i18next's no-instance `t` returns the `defaultValue` **verbatim, without
 * interpolating it**. A nav-kebab trigger whose default is `Actions for` plus a
 * `{{label}}` placeholder therefore rendered that placeholder LITERALLY into an
 * `aria-label` — RECIPE §7 defect class 1, un-interpolated placeholders,
 * introduced by the very change that removed the hard-coded English. Two
 * page-shell suites caught it; nothing else in the package would have.
 *
 * It lives in a ZERO-IMPORT leaf (`utils/fallbackTranslator.js`) because the
 * plain modules that take `t` as a parameter need exactly the same behaviour for
 * their optional-`t` arm, and they are documented as React-free — importing it
 * from this module would drag `react-i18next` into their graphs.
 */
import { fallbackT } from '../utils/fallbackTranslator.js';

/**
 * Translator for the `shell` namespace.
 *
 * ⚠️ Hook-placement rule (AAS-I18N-002 RECIPE §3): put this call **immediately
 * above the first computed hook that consumes `t`**. Higher up and
 * `web/component-state-order` reports every `t`-free `useCallback` beneath it;
 * lower down and you ship a TDZ crash that lint scores 0 on.
 *
 * @returns {(key: string, options?: object) => string} `t`, pre-bound to `shell`.
 */
export function useShellTranslation() {
  const { i18n, t } = useTranslation(SHELL_NAMESPACE);
  // `ready` is FALSE for a legitimately-still-loading instance too, so it cannot
  // be the discriminator. react-i18next's no-instance stand-in is a plain object
  // with none of i18next's API on it — probing for a real method is exact.
  return typeof i18n?.getResource === 'function' ? t : fallbackT;
}

export default useShellTranslation;
